import React, { useEffect, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import { Button, Modal, Row, Col, Form, OverlayTrigger } from "react-bootstrap";
import { MdFilterList, MdAddCircle, MdEdit, MdDelete } from "react-icons/md";
import { Tooltip } from "@mui/material";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import loading from "../../assets/images/common/loading.gif";
import AppDataGrid from "../../components/common/AppDataGrid";

const RouteLocation = () => {
    const [routes, setRoutes] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const navigate = useNavigate();
    const [showFilterModal, setShowFilterModal] = useState(false);
    const handleShowFilterModal = () => setShowFilterModal(true);
    const handleCloseFilterModal = () => setShowFilterModal(false);
    const [isLoading, setIsLoading] = useState(true);

    const baseUrl = process.env.REACT_APP_API_BASE_URL;
    const userData = sessionStorage.getItem("user");
    const userObj = userData ? JSON.parse(userData) : {};

    const readOnlyRoles = ["Class Teacher", "Teacher", "Class Incharge", "School Admin"];
    const canSubmit = !readOnlyRoles.includes(userObj.role_name?.trim());

    const [form, setForm] = useState({
        route_location_id: "",
        location_name: "",

        school_id: userObj?.school_id || ""
    });

    const [filter, setFilter] = useState({
        route_location_id: 0,
        location_name: "",
        status: "",
        action: "FILTER"
    });

    const handleInputChange = (e) => {
        const { id, value } = e.target;
        setForm((prevForm) => ({
            ...prevForm,
            [id]: value
        }));
    };

    const fetchRouteLocations = async () => {
        try {
            const response = await axios.post(`${baseUrl}/routelocation/`, {
                action: "READ",
                school_id: userObj.school_id
            });
            setRoutes(response.data);
        } catch (error) {
            console.error("Error fetching route locations:", error);
        }
    };

    // const handleFilterSearch = async () => {
    //     try {
    //         const response = await axios.post(`${baseUrl}/routelocation/`, {
    //             action: "FILTER",
    //             school_id: userObj.school_id,
    //             ...filter
    //         });
    //         setRoutes(response.data);
    //         setShowFilterModal(false);
    //     } catch (error) {
    //         console.error("Error filtering route locations:", error);
    //     }
    // };

    const handleFilterSearch = async (e) => {
        e.preventDefault();
        const formData = {
            route_location_id: filter.route_location_id || 0,
            location_name: filter.location_name || "",
            school_id: userObj.school_id,
            action: "FILTER",
        };
        try {
            const response = await axios.post(baseUrl + "/routelocation/", formData, {
                headers: {
                    "Content-Type": "application/json",
                },
            });
            setRoutes(response.data || []);
            handleCloseFilterModal();
        } catch (error) {
            console.error("Error fetching data:", error);
        }
        // }
    };



    const handleEditClick = (route_location_id) => {
        const selected = routes.find(r => r.route_location_id === route_location_id);
        if (selected) {
            navigate("/addlocation", { state: { routeData: selected } });
        } else {
            console.error(`Route Location ID ${route_location_id} not found`);
        }
    };

    const handleDeleteClick = async (route_location_id) => {
        if (!window.confirm("Are you sure you want to delete this route location?")) return;

        try {
            await axios.post(`${baseUrl}/routelocation/`, {
                action: "DELETE",
                route_location_id,
                school_id: userObj.school_id
            });
            toast.success("Route location deleted");
            fetchRouteLocations();
        } catch (error) {
            console.error("Error deleting route location:", error);
        }
    };

    const searchableColumns = [
        (row) => row.location_name,
        (row) => row.status
    ];

    const filteredRecords = (routes || []).filter((item) =>
        searchableColumns.some((selector) => {
            const value = selector(item);
            const stringValue = String(value || '').toUpperCase().replace(/[-\s]+/g, '');
            const normalizedQuery = searchQuery.toUpperCase().replace(/[-\s]+/g, '');
            return stringValue.includes(normalizedQuery);
        })
    );

    const columns = [
        {
            field: "location_name",
            headerName: "Location Name",
            flex: 1,
            renderCell: (params) => (
                <Tooltip title={params.value || ""}>
                    <span className="cell-wrap">{params.value}</span>
                </Tooltip>
            )
        },
        {
            field: "status",
            headerName: "Status",
            flex: 1,
            renderCell: (params) => (
                <Tooltip title={params.value || ""}>
                    <span className="cell-wrap">{params.value}</span>
                </Tooltip>
            )
        },
        ...(canSubmit
            ? [
                {
                    field: "actions",
                    headerName: "Actions",
                    flex: 1,
                    sortable: false,
                    renderCell: (params) =>
                        params.row.route_location_id !== "No records found" ? (
                            <div className="tableActions">
                                <Tooltip title="Edit" arrow>
                                    <span
                                        className="commonActionIcons"
                                        onClick={() => handleEditClick(params.row.route_location_id)}
                                    >
                                        <MdEdit />
                                    </span>
                                </Tooltip>
                                <Tooltip title="Delete" arrow>
                                    <span
                                        className="commonActionIcons"
                                        onClick={() => handleDeleteClick(params.row.route_location_id)}
                                    >
                                        <MdDelete />
                                    </span>
                                </Tooltip>
                            </div>
                        ) : null
                }
            ]
            : [])
    ];

    const handleReset = async (e) => {
        e.preventDefault();
        setFilter({
            route_location_id: "",
            location_name: "",
            status: ""
        });
        setShowFilterModal(true);
        fetchRouteLocations();
    };

    const handleSearchChange = (event) => {
        setSearchQuery(event.target.value);
    };

    useEffect(() => {
        setIsLoading(true);
        fetchRouteLocations().finally(() => setIsLoading(false));
    }, []);

    return (
        <div className="pageBody">
            <ToastContainer />
            <div className="commonDataTableHead">
                <div className="d-flex justify-content-between align-items-center w-100">
                    <div className="d-flex align-items-center" style={{ gap: "10px" }}>
                        <h6 className="commonTableTitle">Route Locations</h6>
                    </div>
                    <div>
                        <input
                            type="text"
                            placeholder="Search..."
                            value={searchQuery}
                            className="searchInput"
                            onChange={handleSearchChange}
                        />
                    </div>
                    <div className="d-flex align-items-center" style={{ gap: 6 }}>
                        <OverlayTrigger placement="top" overlay={<Tooltip id="tooltip-top">Filter</Tooltip>}>
                            <Button className="secondaryBtn" variant="secondary" onClick={handleShowFilterModal}>
                                <MdFilterList />
                            </Button>
                        </OverlayTrigger>
                        <OverlayTrigger placement="top" overlay={<Tooltip id="tooltip-top">Add</Tooltip>}>
                            <Button className="primaryBtn" variant="primary" onClick={() => navigate("/addlocation")}>
                                <MdAddCircle />
                            </Button>
                        </OverlayTrigger>
                    </div>
                </div>
            </div>

            <div className="commonTable height100">
                <div className="tableBody">
                    {isLoading ? (
                        <div className="loadingContainer">
                            <img src={loading} alt="Loading..." className="loadingGif" />
                        </div>
                    ) : (
                        <AppDataGrid
                            rows={
                                Array.isArray(filteredRecords) && filteredRecords.length > 0
                                    ? filteredRecords
                                    : [{ route_location_id: "No records found", location_name: "No records found" }]
                            }
                            columns={columns}
                            getRowId={(row) => row.route_location_id}
                            showToolbar={false}
                            pagination
                            pageSizeOptions={[10, 20, 50]}
                            density="compact"
                            maxHeight={550}
                        />
                    )}
                </div>
            </div>

            {/* Filter Modal */}
            <Modal show={showFilterModal} onHide={handleCloseFilterModal} className="commonFilterModal">
                <Modal.Header closeButton className="modalHeaderFixed">
                    <Modal.Title>Filter</Modal.Title>
                </Modal.Header>
                <Modal.Body className="modalBodyScrollable">
                    <Row>
                        <Col xs={12}>
                            <div className="commonInput">
                                <Form.Group controlId="location_name">
                                    <Form.Label>Location Name</Form.Label>
                                    <Form.Control
                                        type="text"
                                        id="location_name"
                                        value={filter.location_name}
                                        placeholder="Enter Location Name"
                                        onChange={(e) => setFilter({ ...filter, location_name: e.target.value })}
                                    />
                                </Form.Group>
                            </div>
                        </Col>
                        {/* <Col xs={12}>
                            <div className="commonInput">
                                <Form.Group controlId="status">
                                    <Form.Label>Status</Form.Label>
                                    <Form.Control
                                        type="text"
                                        id="status"
                                        value={filter.status}
                                        placeholder="Enter Status"
                                        onChange={(e) => setFilter({ ...filter, status: e.target.value })}
                                    />
                                </Form.Group>
                            </div>
                        </Col> */}
                    </Row>
                </Modal.Body>
                <Modal.Footer className="modalFooterFixed">
                    <Button variant="secondary" className="btn-info clearBtn me-2" onClick={handleReset}>
                        Reset
                    </Button>
                    <div>
                        <Button variant="secondary" className="btn-danger secondaryBtn me-2" onClick={handleCloseFilterModal}>
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            className="btn-success primaryBtn"
                            onClick={handleFilterSearch}
                        >
                            Search
                        </Button>
                    </div>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default RouteLocation;
